/**
 * Room Merge Notification Component
 * 
 * Shows a toast-style notification when rooms are automatically merged
 */

import React, { useEffect } from 'react';
import { Link2, Check } from 'lucide-react';

interface RoomMergeNotificationProps {
  message: string;
  roomCount: number;
  wallCount: number;
  onClose: () => void;
  duration?: number;
}

const RoomMergeNotification: React.FC<RoomMergeNotificationProps> = ({
  message,
  roomCount,
  wallCount,
  onClose,
  duration = 4000
}) => {
  // Auto-close after duration
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const wallWord = wallCount === 1 ? 'wall' : 'walls';

  return (
    <div className="fixed top-4 right-4 z-[10001] animate-slideInRight">
      <div className="bg-white rounded-lg shadow-2xl border-2 border-blue-500 p-4 min-w-[300px] max-w-[400px]">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Link2 className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-gray-900">
              Rooms Merged!
            </div>
            <div className="text-xs text-gray-600">
              {roomCount} rooms combined
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ×
          </button>
        </div>

        {/* Message */}
        <div className="bg-blue-50 rounded p-3 mb-3">
          <div className="text-sm text-gray-700">
            {message}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            Removed {wallCount} shared {wallWord}
          </div>
        </div>

        {/* Success indicator */}
        <div className="flex items-center gap-2 text-xs text-green-600">
          <Check className="w-4 h-4" />
          <span>Rooms successfully merged</span>
        </div>

        {/* Progress bar */}
        <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-500 animate-shrinkWidth"
            style={{ animationDuration: `${duration}ms` }}
          />
        </div>
      </div>
    </div>
  );
};

export default RoomMergeNotification;
